import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import { LegalFooter } from "@/components/brand/LegalFooter";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { checkRateLimit, recordAttempt, rateLimitMessage } from "@/lib/security/rateLimit";
import { BrandSlogan } from "@/components/brand/BrandSlogan";
import { signUpEmail, signInGoogle } from "@/lib/auth/authClient";
import { translateAuthError } from "@/lib/auth/authErrorTranslator";
import { readPendingPrediction, PENDING_XP_AMOUNT, suggestedNationLabel } from "@/lib/predictions/guestPrediction";
import { Sparkles } from "lucide-react";
import { TurnstileGate } from "@/components/security/TurnstileGate";

export const Route = createFileRoute("/registro")({
  head: () => ({
    meta: [
      { title: "Registro · VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { name: "description", content: "Crea tu cuenta VerseSport — Inteligencia Deportiva en Tiempo Real." },
    ],
  }),
  component: RegistrationPage,
});

// Express registration (Phase 2) — minimal fields to maximize conversion.
// Nation, name and extras are collected later (NationGate, /ajustes).
const schema = z.object({
  email: z.string().trim().email().max(255),
  password: z.string().min(6).max(128),
  password_confirm: z.string().min(6).max(128),
  terms_accepted: z.literal(true),
}).refine((d) => d.password === d.password_confirm, {
  path: ["password_confirm"],
  message: "Las contraseñas no coinciden",
});

function RegistrationPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending] = useState(() => readPendingPrediction());
  const pendingNation = pending ? suggestedNationLabel(pending.suggested_nation) : null;
  // PR-34A — Turnstile verification. Defaults to true so the registration UX
  // is unchanged when no site key is configured; the widget flips it back to
  // false the moment a real challenge is mounted and re-verifies on success.
  const [verified, setVerified] = useState(true);

  useEffect(() => {
    void trackEvent("registration_started", {});
    if (pending) {
      void trackEvent("guest_prediction_registered", {
        choice: pending.choice,
        pending_xp: pending.pending_xp,
        suggested_nation: pending.suggested_nation,
      });
    }
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const decision = checkRateLimit("/api/register");
    if (!decision.ok) {
      toast.error(rateLimitMessage());
      return;
    }
    recordAttempt("/api/register");
    if (!verified) {
      toast.error("Verifica que no eres un robot");
      return;
    }
    const fd = new FormData(e.currentTarget);
    const payload = {
      email: String(fd.get("email") ?? ""),
      password: String(fd.get("password") ?? ""),
      password_confirm: String(fd.get("password_confirm") ?? ""),
      terms_accepted: fd.get("terms_accepted") === "on",
    };
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const k = String(issue.path[0]);
        errs[k] = k === "terms_accepted"
          ? t("reg_must_accept")
          : k === "email"
            ? t("reg_invalid_email")
            : k === "password"
              ? t("auth_password_min")
              : k === "password_confirm"
                ? t("auth_passwords_no_match")
                : t("reg_required");
      }
      setErrors(errs);
      toast.error(
        errs.terms_accepted ??
        errs.email ??
        errs.password ??
        errs.password_confirm ??
        t("reg_required")
      );
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const email = parsed.data.email.trim().toLowerCase();
      const displayName = email.split("@")[0] ?? "fan";
      await signUpEmail({
        email,
        password: parsed.data.password,
        display_name: displayName,
        language: lang,
      });

      void trackEvent("registration_completed", { language: lang });
      void trackEvent("registration_gate_completed", { action: "registered" });
      if (pending) {
        let variant: "A" | "B" | null = null;
        try {
          const v = window.localStorage.getItem("vs_claim_xp_variant");
          if (v === "A" || v === "B") variant = v;
        } catch { /* noop */ }
        void trackEvent("claim_xp_signup_completed", {
          variant,
          choice: pending.choice,
          suggested_nation: pending.suggested_nation,
        });
      }
      try { window.localStorage.setItem("vs:lang", lang); } catch { /* noop */ }
      toast.success(t("reg_success"));
      void navigate({ to: "/verify-email", search: { email } });
    } catch (err) {
      console.warn("[registration] error", err);
      const msg = translateAuthError(err, lang);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const errClass = (k: string) => (errors[k] ? "border-destructive" : "border-border");

  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/welcome" />
      <div className="px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-xl">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-primary">
          {t("fan_badge_personal")}
        </span>
        <h1 className="mt-2 text-2xl font-black text-foreground">{t("reg_fan_title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("reg_fan_sub")}</p>
        <BrandSlogan section="register" size="sm" align="left" withProduct className="mt-2" />

        {pending && (
          <div className="mt-4 rounded-xl border border-amber-400/40 bg-amber-400/10 p-3">
            <div className="flex items-center gap-2 text-sm font-black text-amber-500">
              <Sparkles className="h-4 w-4" />
              Tu predicción fue guardada · +{PENDING_XP_AMOUNT} XP pendientes
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {pendingNation
                ? `Al crear tu cuenta reclamas tus puntos para ${pendingNation.flag} ${pendingNation.name}. Podrás cambiar de nación después.`
                : "Al crear tu cuenta reclamas tus puntos automáticamente."}
            </p>
          </div>
        )}

        {/* W-13 — Google is the highest-converting path. Pinned above
            email to reduce friction; legal terms remain below. */}
        <button
          type="button"
          disabled={googleLoading}
          onClick={async () => {
            setGoogleLoading(true);
            try {
              const res = await signInGoogle();
              if (res?.error) toast.error(res.error.message);
            } catch (e) {
              toast.error(translateAuthError(e, lang));
            } finally {
              setGoogleLoading(false);
            }
          }}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-3 text-sm font-bold text-foreground shadow-sm hover:bg-accent/30 disabled:opacity-60"
        >
          {googleLoading ? "..." : "🚀 Continuar con Google"}
        </button>
        <div className="my-4 flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <div className="h-px flex-1 bg-border" /> o con email <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <Field label={t("reg_email")} error={errors.email}>
            <input name="email" type="email" required maxLength={255} className={`w-full rounded-md border bg-background px-3 py-2 text-sm ${errClass("email")}`} />
          </Field>
          <Field label={t("auth_password_label")} error={errors.password}>
            <input name="password" type="password" required minLength={6} maxLength={128} className={`w-full rounded-md border bg-background px-3 py-2 text-sm ${errClass("password")}`} />
          </Field>
          <Field label={t("auth_password_confirm_label")} error={errors.password_confirm}>
            <input name="password_confirm" type="password" required minLength={6} maxLength={128} className={`w-full rounded-md border bg-background px-3 py-2 text-sm ${errClass("password_confirm")}`} />
          </Field>

          <div className="space-y-2 rounded-md border border-border/60 bg-accent/10 p-3 text-xs">
            <label
              htmlFor="terms_accepted"
              className={`flex items-start gap-3 py-2 cursor-pointer select-none ${errors.terms_accepted ? "text-destructive" : ""}`}
            >
              <input
                id="terms_accepted"
                type="checkbox"
                name="terms_accepted"
                className="h-5 w-5 mt-0.5 shrink-0 accent-primary"
              />
              <span className="leading-snug">{t("reg_terms_opt")}</span>
            </label>
            <p className="pl-6 text-[11px] text-muted-foreground">
              <Link to="/terminos" className="font-bold text-primary hover:underline">{t("legal_terms_link")}</Link>
              <span className="px-1">·</span>
              <Link to="/privacidad" className="font-bold text-primary hover:underline">{t("legal_privacy_link")}</Link>
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow disabled:opacity-60"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            <span>{t("reg_submit")}</span>
          </button>
          <TurnstileGate action="registro" onVerified={setVerified} />
          <p className="text-center text-[11px] text-muted-foreground">
            Después elegirás tu nación al entrar a Nation Wars, Quinielas o Creator Studio.
          </p>
        </form>
        <LegalFooter />
      </div>
      </div>
    </main>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
      {error && <span className="mt-1 block text-[10px] font-bold text-destructive">{error}</span>}
    </label>
  );
}